/**
 * EAA-1804: Shared Ant Design theme preset — the EMG Portal component sheet
 * expressed as an antd `ThemeConfig`.
 *
 * This is the highest-leverage half of "apply the design system": web-content
 * renders through antd's `ConfigProvider`, so every control in the app takes
 * these values without a single per-component edit. Everything here traces to
 * a specific line of the handoff's `COMPONENTS.md`; where a number looks
 * arbitrary it is quoted in a comment.
 *
 * Colors always come from the passed `Colors` object (itself the sheet's
 * palette — see `shared-styles/colors.ts`) or a documented derivation. Never a
 * hardcoded hex: a tenant's white-label override flows through `Colors`, and a
 * literal here would silently ignore it.
 *
 * The sheet's sizing invariant applies throughout: one box size per component
 * across all states. State is expressed with color and inset rings, never by
 * changing a control's footprint — which is why the focus treatment is a ring
 * rather than antd's default outline growth.
 */
import type { ThemeConfig } from 'antd';
import { darkenHex, type Colors } from '@ai-content-hub/shared-styles';

/**
 * EAA-1804: Convert a hex color to an `rgba()` string at the given alpha.
 * `darkenHex`/`lightenHex` (shared-styles) only mix toward black/white — they
 * can't express a translucent tint. The sheet's rings and row tints are all
 * low-alpha washes of the brand color, which must stay translucent so they
 * compose over whatever row background they land on.
 */
function hexToRgba(hex: string, alpha: number): string {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return hex;

    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Sheet geometry that is not derived from `Colors` — see COMPONENTS.md. */
const SHEET = {
    /** Heights: 34 dense, 36 default, 44 table header. */
    controlHeight: 36,
    controlHeightSM: 34,
    controlHeightLG: 40,
    tableHeaderHeight: 44,
    /** Radii: 4 chips-in-table, 6 buttons/nav, 8 cards/inputs, 10 modals, 12 frames. */
    radiusXS: 4,
    radiusSM: 6,
    radius: 8,
    radiusLG: 10,
    /** Transitions 120ms ease-out on color/shadow only, never on size. */
    motion: 0.12,
} as const;

export const createAntdTheme = (colors: Colors): ThemeConfig => {
    // Focus ring: `0 0 0 3px rgba(0,127,163,.25)` — a tint of the brand accent,
    // so a white-labelled tenant's focus ring follows its own brand.
    const focusRing = hexToRgba(colors.cta, 0.25);
    // Danger focus is the same idea at a lower alpha: `rgba(212,24,61,.12)`.
    const focusRingDanger = hexToRgba(colors.error, 0.12);
    // Row/menu tints. The sheet uses a distinct alpha per interaction level;
    // these are the three antd actually exposes hooks for.
    const tintRowSelected = hexToRgba(colors.cta, 0.05);
    const tintMenuActive = hexToRgba(colors.cta, 0.06);
    const tintNavActive = hexToRgba(colors.cta, 0.12);

    return {
        token: {
            // Colors
            colorPrimary: colors.cta,
            colorPrimaryHover: darkenHex(colors.cta, 8),
            colorPrimaryActive: darkenHex(colors.cta, 14),
            colorLink: colors.primary,
            colorLinkHover: colors.primary,
            colorSuccess: colors.success,
            colorWarning: colors.warning,
            colorError: colors.error,
            colorInfo: colors.info,

            // Background
            colorBgContainer: colors.surface,
            colorBgLayout: colors.background,
            colorBgBase: colors.surface,
            colorBgElevated: colors.surfaceElevated,
            // The sheet's "fill" — inset surfaces, disabled controls, Kbd chips.
            colorFillAlter: colors.fill,
            colorFillSecondary: colors.fill,
            colorBgMask: 'rgba(16, 24, 40, 0.45)', // scrim, fixed by the sheet

            // Text — four tiers, matching the sheet's ramp. Three was one too
            // few: label text (#52606D) and metadata (#64707D) are different
            // roles, and collapsing them flattened every form.
            colorText: colors.text,
            colorTextSecondary: colors.textSecondary,
            colorTextTertiary: colors.textMuted,
            colorTextQuaternary: colors.textLight,
            colorTextPlaceholder: colors.textLight,
            colorTextDisabled: colors.textLight,
            colorTextHeading: colors.text,
            colorTextDescription: colors.textMuted,

            // Border — `border` is the hairline, `divider` the lighter rule
            // used inside cards and menus.
            colorBorder: colors.border,
            colorBorderSecondary: colors.divider,
            colorSplit: colors.divider,

            // Radii
            borderRadius: SHEET.radius,
            borderRadiusLG: SHEET.radiusLG,
            borderRadiusSM: SHEET.radiusSM,
            borderRadiusXS: SHEET.radiusXS,

            // Font. Open Sans is retained deliberately: the handoff specifies
            // Nunito, but the typeface is a brand decision, not a styling one,
            // and swapping it is not this preset's call to make.
            fontFamily:
                "'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
            fontFamilyCode: 'ui-monospace, Menlo, Consolas, monospace',
            // Type scale: body 15/UI 14, section 16-18, page titles 24-28.
            fontSize: 14,
            fontSizeSM: 13,
            fontSizeLG: 15,
            fontSizeHeading1: 30,
            fontSizeHeading2: 24,
            fontSizeHeading3: 18,
            fontSizeHeading4: 16,
            fontSizeHeading5: 14,
            lineHeight: 1.55,

            // Spacing — the sheet's ramp includes the odd steps (6/10/14/18/20)
            // that a doubling scale rounds away.
            marginXXS: 4,
            marginXS: 8,
            marginSM: 12,
            margin: 16,
            marginMD: 20,
            marginLG: 24,
            marginXL: 32,
            paddingXXS: 4,
            paddingXS: 8,
            paddingSM: 12,
            padding: 16,
            paddingMD: 20,
            paddingLG: 24,

            // Controls
            controlHeight: SHEET.controlHeight,
            controlHeightSM: SHEET.controlHeightSM,
            controlHeightLG: SHEET.controlHeightLG,
            controlOutline: focusRing,
            controlOutlineWidth: 3,
            controlItemBgActive: tintMenuActive,
            controlItemBgHover: colors.surfaceHover,

            // Motion — color and shadow only. A transition on a control's
            // footprint is what makes a hover read as a layout bug.
            motionDurationMid: `${SHEET.motion}s`,
            motionDurationFast: '0.1s',

            // Shadows: card is a hairline ring, not an elevation.
            boxShadow: '0 0 0 1px rgba(31, 41, 51, 0.05)',
            boxShadowSecondary: '0 0 0 1px #E5E7EB, 0 16px 40px rgba(16, 24, 40, 0.14)',
            boxShadowTertiary: '0 4px 12px rgba(16, 24, 40, 0.08)',

            wireframe: false,
        },
        components: {
            // Table — 44px neutral header, 13px/500 secondary text.
            Table: {
                headerBg: colors.tableHeaderBg,
                headerColor: colors.tableHeaderText,
                headerSplitColor: colors.divider,
                headerBorderRadius: SHEET.radius,
                borderColor: colors.divider,
                rowHoverBg: colors.surfaceHover,
                rowSelectedBg: tintRowSelected,
                rowSelectedHoverBg: tintRowSelected,
                cellPaddingBlock: 12,
                cellPaddingInline: 20,
                cellFontSize: 14,
                footerBg: colors.surface,
                footerColor: colors.textMuted,
            },
            // Button — only `type="primary"` carries brand color; `default` is
            // a neutral outline. Every button is flat (the sheet has no button
            // shadow), and hover darkens the border to the control ring.
            Button: {
                fontWeight: 500,
                primaryColor: colors.textOnCta,
                solidTextColor: colors.textOnCta,
                primaryShadow: 'none',
                defaultShadow: 'none',
                dangerShadow: 'none',
                defaultBg: colors.surface,
                defaultColor: colors.text,
                defaultBorderColor: colors.border,
                defaultHoverBg: colors.surfaceHover,
                defaultHoverColor: colors.text,
                defaultHoverBorderColor: colors.controlRing,
                defaultActiveBg: colors.surfaceHover,
                defaultActiveColor: colors.text,
                defaultActiveBorderColor: colors.controlRing,
                defaultBgDisabled: colors.fill,
                borderColorDisabled: colors.border,
                colorTextDisabled: colors.textLight,
                paddingInline: 16,
                paddingInlineSM: 14,
                contentFontSize: 14,
            },
            // Card — a 1px ring, not an elevated surface.
            Card: {
                headerBg: colors.surface,
                headerFontSize: 16,
                borderRadiusLG: SHEET.radius,
                boxShadowTertiary: 'none',
                paddingLG: 20,
            },
            Input: {
                borderRadius: SHEET.radius,
                activeBorderColor: colors.cta,
                hoverBorderColor: colors.controlRing,
                activeShadow: `0 0 0 3px ${focusRing}`,
                errorActiveShadow: `0 0 0 3px ${focusRingDanger}`,
                colorBgContainerDisabled: colors.fill,
                paddingInline: 12,
            },
            InputNumber: {
                borderRadius: SHEET.radius,
                activeBorderColor: colors.cta,
                hoverBorderColor: colors.controlRing,
                activeShadow: `0 0 0 3px ${focusRing}`,
            },
            Select: {
                borderRadius: SHEET.radius,
                optionSelectedBg: tintMenuActive,
                optionSelectedColor: colors.text,
                optionActiveBg: colors.surfaceHover,
                optionPadding: '9px 12px',
                colorBorder: colors.border,
                // Menu panel takes the popover shadow, not the card ring.
                boxShadowSecondary: '0 0 0 1px #E5E7EB, 0 16px 40px rgba(16, 24, 40, 0.14)',
            },
            DatePicker: {
                borderRadius: SHEET.radius,
                activeBorderColor: colors.cta,
                hoverBorderColor: colors.controlRing,
                cellActiveWithRangeBg: colors.ctaLight,
            },
            // Modal — 520px / radius 10 / the sheet's modal shadow.
            Modal: {
                borderRadiusLG: SHEET.radiusLG,
                contentBg: colors.surfaceElevated,
                headerBg: colors.surfaceElevated,
                titleFontSize: 17,
                boxShadow: '0 20px 48px rgba(16, 24, 40, 0.28)',
            },
            // Drawer — right-side 400–480, sticky header/footer.
            Drawer: {
                borderRadiusLG: 0,
                colorBgElevated: colors.surfaceElevated,
                paddingLG: 20,
            },
            // Sidebar nav.
            Menu: {
                itemSelectedBg: colors.sidebarMenuSelectedBg,
                itemSelectedColor: colors.sidebarMenuSelectedText,
                subMenuItemSelectedColor: colors.sidebarMenuSelectedText,
                itemHoverBg: colors.surfaceHover,
                itemActiveBg: tintNavActive,
                itemBorderRadius: SHEET.radiusSM,
                itemHeight: 36,
                iconSize: 18,
                collapsedIconSize: 18,
                activeBarWidth: 3,
                groupTitleColor: colors.textMuted,
                groupTitleFontSize: 11,
            },
            Layout: {
                headerBg: colors.surface,
                siderBg: colors.surface,
                bodyBg: colors.background,
                headerHeight: 56,
                headerPadding: '0 24px',
            },
            // Segmented — the sheet is explicit that active uses the brand
            // teal, NOT a neutral dark fill: a dark fill reads as a
            // disabled/inverted state next to the teal-accented nav.
            Segmented: {
                itemSelectedBg: colors.cta,
                itemSelectedColor: colors.textOnCta,
                itemColor: colors.textSecondary,
                itemHoverBg: colors.surfaceHover,
                trackBg: colors.surface,
                trackPadding: 3,
                borderRadius: SHEET.radius,
                borderRadiusSM: SHEET.radiusSM,
            },
            // Tabs — underline strip, 2px active border, gap 22.
            // EAA-1817: tuned to Portal 6 frame 47-52's app-detail tab bar
            // (the frame this token block's own consumer, the new `Tabs` kit
            // component, was built for) — inactive `#64707d` (`textMuted`),
            // active `#006888` (reusing `sidebarMenuSelectedText`, the one
            // existing token at that exact hex), 22px gutter per the frame's
            // `margin-right:22px`. No consumer used the previous
            // textSecondary/text/24px values (nothing in the app imports antd
            // `Tabs` directly yet), so this is a pure fidelity fix, not a
            // behavior change for any existing screen.
            Tabs: {
                itemColor: colors.textMuted,
                itemSelectedColor: colors.sidebarMenuSelectedText,
                itemHoverColor: colors.text,
                inkBarColor: colors.cta,
                horizontalItemGutter: 22,
                horizontalItemPadding: '0 2px 10px',
                titleFontSize: 14,
            },
            // Status pills: 12px/600, radius 999, nowrap.
            Tag: {
                borderRadiusSM: 999,
                defaultBg: colors.fill,
                defaultColor: colors.textSecondary,
                fontSize: 12,
            },
            Badge: {
                textFontSize: 11,
                textFontWeight: 700,
                indicatorHeight: 18,
                indicatorHeightSM: 14,
            },
            Tooltip: {
                // Dark chip on light UI — fixed by the sheet, so it reads the
                // same in both themes rather than inverting with the palette.
                colorBgSpotlight: colors.text,
                colorTextLightSolid: colors.surface,
                borderRadius: SHEET.radiusSM,
                fontSize: 12,
            },
            Popover: {
                borderRadiusLG: SHEET.radius,
                colorBgElevated: colors.surfaceElevated,
            },
            Dropdown: {
                borderRadiusLG: SHEET.radius,
                controlItemBgHover: tintMenuActive,
                controlItemBgActive: tintMenuActive,
                paddingBlock: 8,
                colorBgElevated: colors.surfaceElevated,
            },
            // Switch — 30x17 track, 13px knob.
            Switch: {
                trackHeight: 17,
                trackMinWidth: 30,
                handleSize: 13,
                colorPrimary: colors.cta,
                colorTextQuaternary: colors.controlRing,
            },
            Checkbox: {
                borderRadiusSM: SHEET.radiusXS,
                colorBorder: colors.controlRing,
                controlInteractiveSize: 16,
            },
            Radio: {
                colorBorder: colors.controlRing,
                radioSize: 16,
                dotSize: 6,
            },
            // Progress — 4px track; the bar color is chosen by the caller
            // (success >= 50%, warning below, border at 0).
            Progress: {
                defaultColor: colors.success,
                remainingColor: colors.divider,
                lineBorderRadius: 999,
            },
            Pagination: {
                itemActiveBg: colors.surface,
                borderRadius: SHEET.radiusSM,
                itemSize: 32,
                colorText: colors.textSecondary,
            },
            Alert: {
                borderRadiusLG: SHEET.radius,
                colorSuccessBg: colors.successLight,
                colorSuccessBorder: colors.success,
                colorWarningBg: colors.warningBg,
                colorWarningBorder: colors.warning,
                colorErrorBg: colors.errorBg,
                colorErrorBorder: colors.error,
                colorInfoBg: colors.infoBg,
                colorInfoBorder: colors.infoBorder,
            },
            // Skeleton — two-tone, no shimmer; the palette carries the state.
            Skeleton: {
                color: colors.fill,
                colorGradientEnd: colors.divider,
                borderRadiusSM: SHEET.radiusXS,
            },
            Spin: {
                colorPrimary: colors.cta,
            },
            Collapse: {
                headerBg: 'transparent',
                headerPadding: '12px 16px',
                contentPadding: '0 16px 14px',
                borderRadiusLG: SHEET.radius,
            },
            Form: {
                labelColor: colors.textSecondary,
                labelFontSize: 13,
                verticalLabelPadding: '0 0 6px',
                itemMarginBottom: 20,
            },
            Divider: {
                colorSplit: colors.border,
            },
            Breadcrumb: {
                itemColor: colors.textSecondary,
                lastItemColor: colors.text,
                separatorColor: colors.textMuted,
                fontSize: 14,
            },
            Avatar: {
                colorTextPlaceholder: colors.ctaLight,
                borderRadius: SHEET.radiusSM,
            },
            Notification: {
                borderRadiusLG: SHEET.radiusLG,
                width: 344,
                colorBgElevated: colors.surfaceElevated,
            },
            Upload: {
                colorBorder: colors.controlRing,
                borderRadiusLG: SHEET.radius,
            },
        },
    };
};
